import { keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from 'hooks/useApiQuery';
import { apiFetch } from 'api/client';
import { INotifications } from 'utils/models';

type RespLoose = any;

const extractList = (val: any): INotifications[] => {
  if (Array.isArray(val)) return val;
  if (Array.isArray(val?.notifications)) return val.notifications;
  if (Array.isArray(val?.data?.notifications)) return val.data.notifications;
  if (Array.isArray(val?.items)) return val.items;
  return [];
};

const rebuildWith = (oldVal: any, updated: INotifications[]) => {
  if (Array.isArray(oldVal)) return updated;
  if (oldVal && typeof oldVal === 'object') {
    if (Array.isArray(oldVal.notifications)) return { ...oldVal, notifications: updated };
    if (oldVal.data && Array.isArray(oldVal.data.notifications)) {
      return { ...oldVal, data: { ...oldVal.data, notifications: updated } };
    }
    if (Array.isArray(oldVal.items)) return { ...oldVal, items: updated };
  }
  return updated;
};

export const useNotifications = (enabled = true) => {
  const qc = useQueryClient();

  const listQ = useApiQuery<RespLoose, INotifications[]>(
    ['notifications'],
    '/api/hazel/doctors/get-notification',
    {
      enabled,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      retry: 1,
      select: (raw) => {
        const d = raw?.data ?? raw ?? {};
        const list = extractList(d);
        return list
          .map((n: any) => {
            const id = n.id ?? n.notification_id ?? n.notificationId ?? n._id ?? n.uuid ?? null;
            if (id == null) return null;
            return {
              ...n,
              id: String(id),
              is_read: n.is_read ?? n.isRead ?? false,
              created_at: n.created_at ?? n.createdAt ?? n.date ?? null
            } as INotifications;
          })
          .filter(Boolean) as INotifications[];
      }
    }
  );

  const markMut = useMutation({
    mutationKey: ['markNotificationRead'],
    mutationFn: async (idRaw: string | number) => {
      const id = encodeURIComponent(String(idRaw));
      return apiFetch(`/api/hazel/doctors/read-notification/${id}`, { method: 'GET' });
    },
    onMutate: async (idRaw) => {
      await qc.cancelQueries({ queryKey: ['notifications'] });
      const previous = qc.getQueryData(['notifications']);

      qc.setQueryData(['notifications'], (oldVal: any) => {
        const base = extractList(oldVal);
        const updated = base.map((n: any) =>
          (n.id ?? n.notification_id ?? n.notificationId) == idRaw ? { ...n, is_read: true } : n
        );
        return rebuildWith(oldVal, updated);
      });

      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous !== undefined) qc.setQueryData(['notifications'], ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }
  });

  const notifications = listQ.data ?? [];
  const hasUnread = notifications.some((n) => !n.is_read);

  const handleReadNotification = async ({
    id,
    is_read,
    link
  }: {
    id: string | number;
    is_read?: boolean;
    link?: string;
  }) => {
    if (!id) return;
    if (!is_read) await markMut.mutateAsync(id);
    if (link) window.location.assign(link);
  };

  return {
    ...listQ,
    notifications,
    hasUnread,
    markAsRead: (id: string | number) => markMut.mutateAsync(id),
    handleReadNotification
  };
};
